import { useState, useEffect, useCallback } from "react";
import { useCompetencyMatchingContext } from "@/contexts/competencyMatchingContext";
import { getAllItemsAtSameLevel } from "../../../../../utils/competencyTree";
import { DomainCompetency } from "Stores/courseStore";
import { MatchResult, calculateSimilarity, getMatchLevel } from "../../../../../utils/similarityUtils";

const CATEGORY_STORAGE_KEY = "competencyMatchingCategories";
const EXPANDED_STORAGE_KEY = "competencyMatchingExpanded";

export const useCategorySelection = () => {
    const [activeKeys, setActiveKeys] = useState<string[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<Record<string, string[]>>({});
    const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
    const [matchResults, setMatchResults] = useState<Record<string, Record<string, MatchResult>>>({});

    const {
        standardsCategoriesLoading,
        categoriesLoading,
        categoriesMutate,
        selectedCategoryData,
        selectedStandardCategoryData
    } = useCompetencyMatchingContext();

    useEffect(() => {
        const savedCategories = localStorage.getItem(CATEGORY_STORAGE_KEY);
        const savedExpanded = localStorage.getItem(EXPANDED_STORAGE_KEY);

        if (savedCategories) {
            try {
                const parsedCategories = JSON.parse(savedCategories);
                setSelectedCategories(parsedCategories);
            } catch (error) {
                console.error("Error loading saved categories:", error);
            }
        }

        if (savedExpanded) {
            try {
                const parsedExpanded = JSON.parse(savedExpanded);
                setExpandedItems(parsedExpanded);
                const expandedKeys = Object.entries(parsedExpanded)
                    .filter(([_, isExpanded]) => isExpanded)
                    .map(([key]) => key);
                setActiveKeys(expandedKeys);
            } catch (error) {
                console.error("Error loading saved expanded items:", error);
            }
        }
    }, []);

    useEffect(() => {
        if (Object.keys(selectedCategories).length > 0) {
            localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(selectedCategories));
        }
    }, [selectedCategories]);

    useEffect(() => {
        if (Object.keys(expandedItems).length > 0) {
            localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(expandedItems));
        }
    }, [expandedItems]);

    useEffect(() => {
        if (!selectedCategoryData && !categoriesLoading) {
            categoriesMutate();
        }
    }, []);

    const handleAccordionToggle = useCallback((ruuid: string) => {
        const newActiveKeys = activeKeys.includes(ruuid)
            ? activeKeys.filter((key) => key !== ruuid)
            : [...activeKeys, ruuid];

        setActiveKeys(newActiveKeys);

        if (!expandedItems[ruuid] && newActiveKeys.includes(ruuid)) {
            setExpandedItems(prev => ({ ...prev, [ruuid]: true }));
        }
    }, [activeKeys, expandedItems]);

    const handleCategoryChange = useCallback(async (
        ruuid: string,
        index: number,
        value: string
    ) => {
        if (!selectedCategoryData) {
            await categoriesMutate();
        }
        setSelectedCategories((prev) => {
            const categories = [...(prev[ruuid] || [""])];
            categories[index] = value;
            return { ...prev, [ruuid]: categories };
        });

        setMatchResults((prev) => {
            const newMatchResults = { ...prev };
            if (!newMatchResults[ruuid]) {
                newMatchResults[ruuid] = {};
            }
            newMatchResults[ruuid][index.toString()] = { matchLevel: "manual", matchPercentage: 0 };
            return newMatchResults;
        });
    }, [selectedCategoryData, categoriesMutate]);

    const addCategory = useCallback((ruuid: string) => {
        setSelectedCategories((prev) => {
            const categories = [...(prev[ruuid] || [""])];
            categories.push("");
            return { ...prev, [ruuid]: categories };
        });

        setMatchResults((prev) => {
            const newMatchResults = { ...prev };
            if (!newMatchResults[ruuid]) {
                newMatchResults[ruuid] = {};
            }
            const newIndex = (selectedCategories[ruuid] || [""]).length;
            newMatchResults[ruuid][newIndex.toString()] = { matchLevel: "manual", matchPercentage: 0 };
            return newMatchResults;
        });
    }, [selectedCategories]);

    const removeCategory = useCallback((ruuid: string, index: number) => {
        setSelectedCategories((prev) => {
            const categories = [...(prev[ruuid] || [""])];
            categories.splice(index, 1);
            return { ...prev, [ruuid]: categories };
        });

        setMatchResults((prev) => {
            const newMatchResults = { ...prev };
            if (newMatchResults[ruuid]) {
                delete newMatchResults[ruuid][index.toString()];

                const reindexedResults: Record<string, MatchResult> = {};
                Object.entries(newMatchResults[ruuid])
                    .filter(([key]) => parseInt(key) > index)
                    .forEach(([key, value]) => {
                        const newKey = (parseInt(key) - 1).toString();
                        reindexedResults[newKey] = value;
                    });

                Object.entries(newMatchResults[ruuid])
                    .filter(([key]) => parseInt(key) < index)
                    .forEach(([key, value]) => {
                        reindexedResults[key] = value;
                    });

                newMatchResults[ruuid] = reindexedResults;
            }
            return newMatchResults;
        });
    }, []);

    const getSelectedCategories = useCallback((ruuid: string): string[] => {
        return selectedCategories[ruuid] || [""];
    }, [selectedCategories]);

    const autoSelectSimilarItems = useCallback(() => {
        if (!selectedCategoryData || !selectedStandardCategoryData) return;

        const processCompetency = (standardItem: DomainCompetency) => {
            const userCompetencies = getAllItemsAtSameLevel(
                selectedCategoryData ? [selectedCategoryData] : [],
                standardItem as unknown as DomainCompetency
            );

            let bestMatch: { ruuid: string; similarity: number } | null = null;

            userCompetencies.forEach(comp => {
                const similarity = calculateSimilarity(
                    standardItem.standard_name,
                    comp.standard_name
                );

                if (similarity >= 30 && (!bestMatch || similarity > bestMatch.similarity)) {
                    bestMatch = { ruuid: comp.ruuid, similarity };
                }
            });

            if (bestMatch) {
                setSelectedCategories(prev => {
                    const currentCategories = prev[standardItem.ruuid] || [""];
                    if (currentCategories[0] === "") {
                        return {
                            ...prev,
                            [standardItem.ruuid]: [bestMatch!.ruuid]
                        };
                    }
                    return prev;
                });
            }

            standardItem.children.forEach(processCompetency);
        };

        processCompetency(selectedStandardCategoryData);
    }, [selectedCategoryData, selectedStandardCategoryData]);

    useEffect(() => {
        if (selectedCategoryData && selectedStandardCategoryData) {
            autoSelectSimilarItems();
        }
    }, [selectedCategoryData, selectedStandardCategoryData, autoSelectSimilarItems]);

    const calculateMatches = useCallback(() => {
        if (!selectedCategoryData || !selectedStandardCategoryData) return;

        const newMatchResults: Record<string, Record<string, MatchResult>> = {};

        const processCompetency = (standardItem: DomainCompetency) => {
            const userCategories = selectedCategories[standardItem.ruuid] || [];

            if (userCategories.length > 0) {
                const userCompetencies = getAllItemsAtSameLevel(
                    selectedCategoryData ? [selectedCategoryData] : [],
                    standardItem as unknown as DomainCompetency
                );

                newMatchResults[standardItem.ruuid] = {};

                userCategories.forEach((category, index) => {
                    if (!category) return;
                    const matchingCompetency = userCompetencies.find(comp => comp.ruuid === category);
                    if (matchingCompetency) {
                        const similarity = calculateSimilarity(
                            standardItem.standard_name,
                            matchingCompetency.standard_name
                        );
                        newMatchResults[standardItem.ruuid][index.toString()] = getMatchLevel(similarity);
                    } else {
                        newMatchResults[standardItem.ruuid][index.toString()] = { matchLevel: "manual", matchPercentage: 0 };
                    }
                });
            }

            standardItem.children.forEach(processCompetency);
        };

        processCompetency(selectedStandardCategoryData);
        setMatchResults(newMatchResults);
    }, [selectedCategories, selectedCategoryData, selectedStandardCategoryData]);

    useEffect(() => {
        calculateMatches();
    }, [calculateMatches, selectedCategories, selectedCategoryData, selectedStandardCategoryData]);

    const getSiblingOptions = useCallback((item: DomainCompetency) => {
        return getAllItemsAtSameLevel(
            selectedCategoryData ? [selectedCategoryData] : [],
            item as unknown as DomainCompetency
        ).map((sibling) => ({
            value: sibling.ruuid,
            label: sibling.standard_name,
        }));
    }, [selectedCategoryData]);

    return {
        activeKeys,
        selectedCategories,
        expandedItems,
        matchResults,
        standardsCategoriesLoading,
        categoriesLoading,
        selectedCategoryData,
        selectedStandardCategoryData,
        handleAccordionToggle,
        handleCategoryChange,
        addCategory,
        removeCategory,
        getSelectedCategories,
        getSiblingOptions
    };
};